import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, LayoutGrid, ListChecks, Store, Check, Settings2, ChevronRight, ShieldCheck, ShieldX, Plus, Minus, Search, Loader2, ArrowLeft } from 'lucide-react';
import { useMutation, usePaginatedQuery } from 'convex/react';
import { api } from '../../../api';
import { useEffectiveSerial } from '@/hooks/useEffectiveSerial';
import { useToast } from '@/hooks/use-toast';
import { useMentorStatus } from '@/hooks/useMentorStatus';
import { useDeviceMutations } from '@/hooks/useDeviceMutations';
import { useAppCategories } from '@/hooks/useAppCategories';
import { useDeviceAppMode } from '@/hooks/useDeviceAppMode';
import { useItunesSearch, ItunesApp } from '@/hooks/useItunesSearch';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Input } from '@/components/ui/input';

const appManagementOptions = [
  {
    id: 'apps-by-category',
    title: 'Apps by Category',
    description: 'Apps are organized into categories like Social, Games, Productivity, and more. You control which categories are allowed. New apps in approved categories are automatically available without individual approval.',
    store: 'Shomri App Store',
    icon: LayoutGrid,
    gradient: 'from-primary to-accent',
  },
  {
    id: 'approved-only',
    title: 'Approved Apps Only',
    description: 'Maximum control over every app. Only apps you specifically approve can be installed. Each new app requires your explicit permission. Best for younger users or when you want complete oversight.',
    store: 'Shomri App Store',
    icon: ListChecks,
    gradient: 'from-primary to-primary-hover',
  },
  {
    id: 'open-blocklist',
    title: 'Open App Store + Block List',
    description: 'Full access to the Apple App Store with the ability to block specific apps. Users can download any app except those you explicitly block. Best for mature users who need minimal restrictions.',
    store: 'Apple App Store',
    icon: Store,
    gradient: 'from-primary/80 to-accent/80',
  },
];

interface CatalogApp {
  id: string;
  name: string;
  iconUrl?: string;
  category?: string;
  bundleId?: string;
}

type RequestAction = {
  type: 'category' | 'whitelist' | 'blacklist';
  action: 'block' | 'allow' | 'add' | 'remove';
  name: string;
  catalogApp?: CatalogApp;
  appId?: string; // For removal operations - stores the Convex app ID
};

const AppManagement: React.FC = () => {
  const navigate = useNavigate();
  const { serial } = useEffectiveSerial();
  const { toast } = useToast();
  const { canChangeDirectly } = useMentorStatus();
  const { updateAppSettings } = useDeviceMutations();
  const { categories, isLoading, allowedApps, blockedApps, deviceAppConfig } = useAppCategories();
  const { currentMode } = useDeviceAppMode();
  
  const [requestAction, setRequestAction] = useState<RequestAction | null>(null);
  const [showItemConfirmation, setShowItemConfirmation] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  
  // App search state
  const [showAppSearchModal, setShowAppSearchModal] = useState(false);
  const [appSearchType, setAppSearchType] = useState<'whitelist' | 'blacklist'>('whitelist');
  const [searchQuery, setSearchQuery] = useState('');
  // When true, show iTunes search for requesting a new app
  const [showItunesSearch, setShowItunesSearch] = useState(false);
  const [isRequestingApp, setIsRequestingApp] = useState(false);

  // Workaround for known Radix UI bug: AlertDialog/Dialog sometimes leaves
  // "pointer-events: none" on <body> after closing, freezing the page.
  // See: https://github.com/radix-ui/primitives/issues/2416
  React.useEffect(() => {
    if (!showItemConfirmation && !showAppSearchModal) {
      const t1 = setTimeout(() => {
        document.body.style.pointerEvents = '';
      }, 300);
      const t2 = setTimeout(() => {
        document.body.style.pointerEvents = '';
      }, 600);
      return () => {
        clearTimeout(t1);
        clearTimeout(t2);
      };
    }
  }, [showItemConfirmation, showAppSearchModal]);

  // Step 1: Search Shomri catalog (public, device-scoped)
  const shomriResults = usePaginatedQuery(
    (api.apps.list as any).allowedByDeviceFilter,
    showAppSearchModal && !showItunesSearch && serial && searchQuery.trim().length >= 2
      ? { serial, query: searchQuery }
      : 'skip',
    { initialNumItems: 20 }
  );

  // Step 2: iTunes search for request flow
  const { results: itunesResults, isLoading: isItunesSearching } = useItunesSearch(
    showAppSearchModal && showItunesSearch ? searchQuery : ''
  );

  // Public mutation to submit app request
  const createAppRequest = useMutation((api.requests.create as any).appRequest);

  // Find the current mode option based on real data
  const currentOption = appManagementOptions.find((opt) => opt.id === currentMode) || appManagementOptions[0];
  const CurrentIcon = currentOption.icon;

  // Derive allowed/blocked from real categories data
  const allowedCategories = categories.filter(c => c.isAllowed);
  const blockedCategories = categories.filter(c => !c.isAllowed);

  // Use the app arrays directly from the hook (already includes name, icon, bundleId)
  const whitelistedApps = allowedApps;
  const blacklistedApps = blockedApps;

  const handleItemRequest = (type: RequestAction['type'], action: RequestAction['action'], name: string, catalogApp?: CatalogApp, appId?: string) => {
    setRequestAction({ type, action, name, catalogApp, appId });
    setShowItemConfirmation(true);
  };

  const handleOpenAppSearch = (type: 'whitelist' | 'blacklist') => {
    setAppSearchType(type);
    setSearchQuery('');
    setShowItunesSearch(false);
    setShowAppSearchModal(true);
  };

  // Step 1: User picks an app from the Shomri catalog — direct add, no upsert needed
  const handleSelectShomriApp = (app: any) => {
    setShowAppSearchModal(false);
    setSearchQuery('');
    setShowItunesSearch(false);
    handleItemRequest(appSearchType, 'add', app.name, {
      id: app.id,
      name: app.name,
      iconUrl: app.iconUrl,
      category: app.category,
      bundleId: app.bundleId,
    });
  };

  // Step 2: User wants to request a new app via iTunes
  const handleRequestItunesApp = async (itunesApp: ItunesApp) => {
    if (!serial) {
      toast({ title: "Error", description: "No device serial found.", variant: "destructive" });
      return;
    }
    setIsRequestingApp(true);
    try {
      await createAppRequest({
        app: itunesApp.bundleId || String(itunesApp.trackId),
        bundleId: itunesApp.bundleId || String(itunesApp.trackId),
        serial,
        name: itunesApp.trackName,
        iconUrl: itunesApp.artworkUrl100,
        category: itunesApp.primaryGenreName,
        trackId: String(itunesApp.trackId),
      } as any);
      toast({
        title: "Request Submitted",
        description: `Your request to add "${itunesApp.trackName}" has been submitted for review.`,
      });
      setShowAppSearchModal(false);
      setSearchQuery('');
      setShowItunesSearch(false);
    } catch (err) {
      toast({ title: "Error", description: "Could not submit request. Please try again.", variant: "destructive" });
    } finally {
      setIsRequestingApp(false);
    }
  };

  const handleConfirmItemRequest = async () => {
    if (!requestAction) return;
    
    // Capture state and close dialog immediately to prevent overlay from blocking UI
    const action = requestAction;
    setShowItemConfirmation(false);
    setRequestAction(null);
    setIsSaving(true);
    
    try {
      let message = '';
      if (canChangeDirectly) {
        const deviceAllowedIds = whitelistedApps.map(a => a.id);
        const deviceBlockedIds = blacklistedApps.map(a => a.id);

        // Build current appFilter
        const currentAppFilter: Record<string, boolean> = {};
        categories.forEach((cat) => {
          currentAppFilter[cat.name] = cat.isAllowed;
        });

        // Actually call the mutation
        if (action.type === 'category') {
          // Apply the requested category change
          if (action.action === 'block') {
            currentAppFilter[action.name] = false;
          } else {
            currentAppFilter[action.name] = true;
          }
          
          // Full-state replacement
          await updateAppSettings({
            allowedApps: deviceAllowedIds as any,
            blockedApps: deviceBlockedIds as any,
            appFilter: currentAppFilter,
          });
          message = `"${action.name}" has been ${action.action === 'block' ? 'blocked' : 'allowed'}.`;
        } else if (action.action === 'add') {
          // Add app to whitelist or blacklist using Convex catalog ID
          if (action.type === 'whitelist' && action.catalogApp) {
            await updateAppSettings({
              allowedApps: [...deviceAllowedIds, action.catalogApp.id] as any,
              blockedApps: deviceBlockedIds as any,
              appFilter: currentAppFilter,
            });
          } else if (action.type === 'blacklist' && action.catalogApp) {
            await updateAppSettings({
              allowedApps: deviceAllowedIds as any,
              blockedApps: [...deviceBlockedIds, action.catalogApp.id] as any,
              appFilter: currentAppFilter,
            });
          }
          message = `"${action.name}" has been added to the ${action.type}.`;
        } else {
          // Remove from list using the stored app ID (which IS a Convex ID)
          if (action.type === 'whitelist' && action.appId) {
            await updateAppSettings({
              allowedApps: deviceAllowedIds.filter(id => id !== action.appId) as any,
              blockedApps: deviceBlockedIds as any,
              appFilter: currentAppFilter,
            });
          } else if (action.type === 'blacklist' && action.appId) {
            await updateAppSettings({
              allowedApps: deviceAllowedIds as any,
              blockedApps: deviceBlockedIds.filter(id => id !== action.appId) as any,
              appFilter: currentAppFilter,
            });
          }
          message = `"${action.name}" has been removed from the ${action.type}.`;
        }
        toast({
          title: "Settings Updated",
          description: message,
        });
      } else {
        // Submit request
        if (action.type === 'category') {
          message = `Your request to ${action.action} "${action.name}" has been submitted.`;
        } else if (action.action === 'add') {
          message = `Your request to add "${action.name}" to the ${action.type === 'whitelist' ? 'whitelist' : 'blacklist'} has been submitted.`;
        } else {
          message = `Your request to remove "${action.name}" from the ${action.type === 'whitelist' ? 'whitelist' : 'blacklist'} has been submitted.`;
        }
        toast({
          title: "Request Submitted",
          description: message,
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update setting. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="min-h-[100dvh] bg-gradient-to-b from-background to-muted/30 pb-28">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background/70 backdrop-blur-2xl border-b border-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.08)]">
        <div className="px-4 py-3 flex items-center gap-3">
          <button
            onClick={() => navigate(`/${serial}/settings`)}
            className="p-2 -ml-2 rounded-full hover:bg-muted/50 active:scale-95 transition-all"
          >
            <ChevronLeft className="w-6 h-6 text-foreground" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-foreground">App Management</h1>
            <p className="text-[13px] text-muted-foreground">How apps are managed on this device</p>
          </div>
        </div>
      </div>

      <div className="px-4 pt-6 space-y-6">
        {/* Current Mode Info with Request Button */}
        <div className="bg-background/60 backdrop-blur-xl rounded-2xl border border-white/15 shadow-[0_4px_16px_rgba(0,0,0,0.08),inset_0_1px_1px_rgba(255,255,255,0.1)] p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3 flex-1">
              <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${currentOption.gradient} flex items-center justify-center shadow-md flex-shrink-0`}>
                <CurrentIcon className="w-5 h-5 text-primary-foreground" />
              </div>
              <div className="min-w-0">
                <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Currently Using</p>
                <p className="font-semibold text-[15px] text-foreground">{currentOption.title}</p>
              </div>
            </div>
            {canChangeDirectly && (
              <button
                onClick={() => navigate(`/${serial}/settings/app-management/mode`)}
                className="px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-[12px] font-medium hover:bg-primary-hover active:scale-[0.97] transition-all flex-shrink-0"
              >
                Change
              </button>
            )}
          </div>
          <p className="text-[13px] text-muted-foreground leading-relaxed mt-3">
            {currentOption.description}
          </p>
        </div>

        {/* Dynamic Content Based on Current Mode */}
        {currentMode === 'apps-by-category' && (
          <Tabs defaultValue="categories" className="w-full">
            <TabsList className="w-full grid grid-cols-2 bg-muted/50 rounded-xl p-1 h-auto">
              <TabsTrigger 
                value="categories" 
                className="rounded-lg py-2.5 text-[14px] font-medium data-[state=active]:bg-background data-[state=active]:shadow-sm"
              >
                Categories
              </TabsTrigger>
              <TabsTrigger 
                value="apps" 
                className="rounded-lg py-2.5 text-[14px] font-medium data-[state=active]:bg-background data-[state=active]:shadow-sm"
              >
                Specific Apps
              </TabsTrigger>
            </TabsList>

            <TabsContent value="categories" className="mt-4 space-y-3">
              <p className="text-[13px] text-muted-foreground px-1">
                {canChangeDirectly 
                  ? "Toggle categories on or off to control which types of apps are available."
                  : "Categories control which types of apps are available."}
              </p>
              
              <div className="bg-background rounded-2xl border border-border/50 shadow-sm overflow-hidden">
                <div className="divide-y divide-border/50">
                  {categories.map((category) => (
                    <div
                      key={category.name}
                      className="w-full px-4 py-3 flex items-center justify-between"
                    >
                      <span className="text-[14px] text-foreground">{category.name}</span>
                      <Switch
                        checked={category.isAllowed}
                        onCheckedChange={async () => {
                          if (!canChangeDirectly) return;
                          try {
                            const newAppFilter: Record<string, boolean> = {};
                            categories.forEach((cat) => {
                              newAppFilter[cat.name] = cat.name === category.name 
                                ? !category.isAllowed 
                                : cat.isAllowed;
                            });
                            await updateAppSettings({
                              allowedApps: whitelistedApps.map(a => a.id) as any,
                              blockedApps: blacklistedApps.map(a => a.id) as any,
                              appFilter: newAppFilter,
                            });
                            toast({
                              title: "Category Updated",
                              description: `${category.name} is now ${!category.isAllowed ? 'allowed' : 'blocked'}.`,
                            });
                          } catch (error) {
                            toast({
                              title: "Error",
                              description: "Failed to update category. Please try again.",
                              variant: "destructive",
                            });
                          }
                        }}
                        disabled={!canChangeDirectly}
                        colorWhenOff="destructive"
                        colorWhenOn="success"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="apps" className="mt-4 space-y-3">
              <p className="text-[13px] text-muted-foreground px-1">
                {canChangeDirectly 
                  ? "Override category settings for specific apps. Tap + to add apps or tap an app to remove it."
                  : "Override category settings for specific apps. Whitelisted apps are always allowed, blacklisted apps are always blocked."}
              </p>

              <Accordion type="single" collapsible className="space-y-3">
                {/* Whitelisted Apps */}
                <AccordionItem value="whitelisted-apps" className="bg-background rounded-2xl border border-border/50 shadow-sm overflow-hidden">
                  <AccordionTrigger className="px-4 py-4 hover:no-underline [&[data-state=open]]:border-b [&[data-state=open]]:border-border/50">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                        <Plus className="w-5 h-5 text-primary" />
                      </div>
                      <div className="text-left">
                        <p className="font-semibold text-[15px] text-foreground">Whitelisted Apps</p>
                        <p className="text-[12px] text-muted-foreground">{whitelistedApps.length} apps always allowed</p>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pb-0">
                    <div className="divide-y divide-border/50">
                      {canChangeDirectly && (
                        <button
                          onClick={() => handleOpenAppSearch('whitelist')}
                          className="w-full px-4 py-3 flex items-center gap-3 text-left hover:bg-muted/50 active:bg-muted transition-colors"
                        >
                          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                            <Plus className="w-5 h-5 text-primary" />
                          </div>
                          <p className="text-[14px] font-medium text-primary">Add App</p>
                        </button>
                      )}
                      {whitelistedApps.length === 0 && !canChangeDirectly ? (
                        <div className="px-4 py-6 text-center text-muted-foreground text-[13px]">
                          No whitelisted apps yet
                        </div>
                      ) : (
                        whitelistedApps.map((app) => (
                          <div
                            key={app.id}
                            className="w-full px-4 py-3 flex items-center justify-between text-left"
                          >
                            <div className="flex items-center gap-3">
                              <img 
                                src={app.iconUrl} 
                                alt={app.name}
                                className="w-10 h-10 rounded-xl object-cover"
                                onError={(e) => { e.currentTarget.src = '/placeholder.svg'; }}
                              />
                              <p className="text-[14px] font-medium text-foreground">{app.name}</p>
                            </div>
                            {canChangeDirectly && (
                              <button
                                onClick={() => handleItemRequest('whitelist', 'remove', app.name, undefined, app.id)}
                                className="p-2 rounded-full hover:bg-destructive/10 active:scale-95 transition-all"
                              >
                                <Minus className="w-4 h-4 text-destructive" />
                              </button>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* Blacklisted Apps */}
                <AccordionItem value="blacklisted-apps" className="bg-background rounded-2xl border border-border/50 shadow-sm overflow-hidden">
                  <AccordionTrigger className="px-4 py-4 hover:no-underline [&[data-state=open]]:border-b [&[data-state=open]]:border-border/50">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-destructive/10 flex items-center justify-center">
                        <Minus className="w-5 h-5 text-destructive" />
                      </div>
                      <div className="text-left">
                        <p className="font-semibold text-[15px] text-foreground">Blacklisted Apps</p>
                        <p className="text-[12px] text-muted-foreground">{blacklistedApps.length} apps always blocked</p>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pb-0">
                    <div className="divide-y divide-border/50">
                      {canChangeDirectly && (
                        <button
                          onClick={() => handleOpenAppSearch('blacklist')}
                          className="w-full px-4 py-3 flex items-center gap-3 text-left hover:bg-muted/50 active:bg-muted transition-colors"
                        >
                          <div className="w-10 h-10 rounded-xl bg-destructive/10 flex items-center justify-center">
                            <Plus className="w-5 h-5 text-destructive" />
                          </div>
                          <p className="text-[14px] font-medium text-destructive">Add App</p>
                        </button>
                      )}
                      {blacklistedApps.length === 0 && !canChangeDirectly ? (
                        <div className="px-4 py-6 text-center text-muted-foreground text-[13px]">
                          No blacklisted apps yet
                        </div>
                      ) : (
                        blacklistedApps.map((app) => (
                          <div
                            key={app.id}
                            className="w-full px-4 py-3 flex items-center justify-between text-left"
                          >
                            <div className="flex items-center gap-3">
                              <img 
                                src={app.iconUrl} 
                                alt={app.name}
                                className="w-10 h-10 rounded-xl object-cover"
                                onError={(e) => { e.currentTarget.src = '/placeholder.svg'; }}
                              />
                              <p className="text-[14px] font-medium text-foreground">{app.name}</p>
                            </div>
                            {canChangeDirectly && (
                              <button
                                onClick={() => handleItemRequest('blacklist', 'remove', app.name, undefined, app.id)}
                                className="p-2 rounded-full hover:bg-destructive/10 active:scale-95 transition-all"
                              >
                                <Minus className="w-4 h-4 text-destructive" />
                              </button>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </TabsContent>
          </Tabs>
        )}

        {currentMode === 'approved-only' && (
          <div className="space-y-3">
            <p className="text-[13px] text-muted-foreground px-1">
              {canChangeDirectly
                ? "Only approved apps can be installed. Tap + to add apps or tap an app to remove it."
                : "Only whitelisted apps can be installed. Request to add new apps to your approved list."}
            </p>
            
            <Accordion type="single" collapsible className="space-y-3">
              <AccordionItem value="approved-apps" className="bg-background rounded-2xl border border-border/50 shadow-sm overflow-hidden">
                <AccordionTrigger className="px-4 py-4 hover:no-underline [&[data-state=open]]:border-b [&[data-state=open]]:border-border/50">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                      <ListChecks className="w-5 h-5 text-primary" />
                    </div>
                    <div className="text-left">
                      <p className="font-semibold text-[15px] text-foreground">Approved Apps</p>
                      <p className="text-[12px] text-muted-foreground">{whitelistedApps.length} apps can be installed</p>
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pb-0">
                  <div className="divide-y divide-border/50">
                    {canChangeDirectly && (
                      <button
                        onClick={() => handleOpenAppSearch('whitelist')}
                        className="w-full px-4 py-3 flex items-center gap-3 text-left hover:bg-muted/50 active:bg-muted transition-colors"
                      >
                        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                          <Plus className="w-5 h-5 text-primary" />
                        </div>
                        <p className="text-[14px] font-medium text-primary">Add App</p>
                      </button>
                    )}
                    {whitelistedApps.length === 0 && !canChangeDirectly ? (
                      <div className="px-4 py-6 text-center text-muted-foreground text-[13px]">
                        No approved apps yet
                      </div>
                    ) : (
                      whitelistedApps.map((app) => (
                        <div
                          key={app.id}
                          className="w-full px-4 py-3 flex items-center justify-between text-left"
                        >
                          <div className="flex items-center gap-3">
                            <img 
                              src={app.iconUrl} 
                              alt={app.name}
                              className="w-10 h-10 rounded-xl object-cover"
                              onError={(e) => { e.currentTarget.src = '/placeholder.svg'; }}
                            />
                            <p className="text-[14px] font-medium text-foreground">{app.name}</p>
                          </div>
                          {canChangeDirectly && (
                            <button
                              onClick={() => handleItemRequest('whitelist', 'remove', app.name, undefined, app.id)}
                              className="p-2 rounded-full hover:bg-destructive/10 active:scale-95 transition-all"
                            >
                              <Minus className="w-4 h-4 text-destructive" />
                            </button>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        )}

        {currentMode === 'open-blocklist' && (
          <div className="space-y-3">
            <p className="text-[13px] text-muted-foreground px-1">
              All apps from the App Store can be downloaded except those on your block list.
            </p>
            
            <Accordion type="single" collapsible className="space-y-3">
              <AccordionItem value="blocked-apps" className="bg-background rounded-2xl border border-border/50 shadow-sm overflow-hidden">
                <AccordionTrigger className="px-4 py-4 hover:no-underline [&[data-state=open]]:border-b [&[data-state=open]]:border-border/50">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-destructive/10 flex items-center justify-center">
                      <Minus className="w-5 h-5 text-destructive" />
                    </div>
                    <div className="text-left">
                      <p className="font-semibold text-[15px] text-foreground">Blocked Apps</p>
                      <p className="text-[12px] text-muted-foreground">{blacklistedApps.length} apps are blocked</p>
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pb-0">
                  <div className="divide-y divide-border/50">
                    {blacklistedApps.length === 0 ? (
                      <div className="px-4 py-6 text-center text-muted-foreground text-[13px]">
                        No blocked apps yet
                      </div>
                    ) : (
                      blacklistedApps.map((app) => (
                        <div
                          key={app.id}
                          className="w-full px-4 py-3 flex items-center text-left"
                        >
                          <div className="flex items-center gap-3">
                            <img 
                              src={app.iconUrl} 
                              alt={app.name}
                              className="w-10 h-10 rounded-xl object-cover"
                              onError={(e) => { e.currentTarget.src = '/placeholder.svg'; }}
                            />
                            <p className="text-[14px] font-medium text-foreground">{app.name}</p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        )}
      </div>

      {/* App Search Modal */}
      <Dialog open={showAppSearchModal} onOpenChange={(open) => {
        setShowAppSearchModal(open);
        if (!open) { setShowItunesSearch(false); setSearchQuery(''); }
      }}>
        <DialogContent className="max-w-[90vw] sm:max-w-md rounded-2xl bg-background max-h-[80vh] flex flex-col">
          <DialogHeader>
            <div className="flex items-center gap-2">
              {showItunesSearch && (
                <button
                  onClick={() => { setShowItunesSearch(false); setSearchQuery(''); }}
                  className="p-1.5 -ml-1 rounded-lg hover:bg-muted/50 active:scale-95 transition-all"
                >
                  <ArrowLeft className="w-5 h-5 text-foreground" />
                </button>
              )}
              <DialogTitle className="text-lg font-bold flex-1">
                {showItunesSearch
                  ? 'Request New App'
                  : appSearchType === 'whitelist' ? 'Add App to Approved List' : 'Add App to Block List'}
              </DialogTitle>
            </div>
            {showItunesSearch && (
              <p className="text-[12px] text-muted-foreground mt-1 pl-0.5">
                Search the Apple App Store and submit a request to add it to Shomri.
              </p>
            )}
          </DialogHeader>
          <div className="flex-1 overflow-hidden flex flex-col">
            {/* Search bar */}
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder={showItunesSearch ? 'Search Apple App Store...' : 'Search Shomri App Store...'}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 rounded-xl"
                autoFocus
              />
            </div>

            <div className="flex-1 overflow-y-auto min-h-0">
              {/* ── STEP 1: Shomri catalog results ── */}
              {!showItunesSearch && (
                <>
                  {shomriResults.status === 'LoadingFirstPage' && (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                    </div>
                  )}
                  {searchQuery.trim().length < 2 && (
                    <p className="text-center text-[13px] text-muted-foreground py-8">
                      Type at least 2 characters to search
                    </p>
                  )}
                  {shomriResults.status !== 'LoadingFirstPage' && searchQuery.trim().length >= 2 && shomriResults.results?.length === 0 && (
                    <p className="text-center text-[13px] text-muted-foreground py-6">
                      No apps found in the Shomri App Store
                    </p>
                  )}
                  {shomriResults.results && shomriResults.results.length > 0 && (
                    <div className="divide-y divide-border/50">
                      {shomriResults.results.map((app: any) => (
                        <button
                          key={app.id}
                          onClick={() => handleSelectShomriApp(app)}
                          className="w-full px-3 py-3 flex items-center gap-3 hover:bg-muted/30 active:scale-[0.99] transition-all text-left"
                        >
                          <img
                            src={app.iconUrl || '/placeholder.svg'}
                            alt={app.name}
                            className="w-10 h-10 rounded-xl object-cover"
                            onError={(e) => { e.currentTarget.src = '/placeholder.svg'; }}
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-[14px] font-medium text-foreground truncate">{app.name}</p>
                            {app.category && (
                              <p className="text-[11px] text-muted-foreground truncate">{app.category}</p>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                  {/* Request new app button */}
                  <div className="pt-4 pb-2 px-1">
                    <button
                      onClick={() => { setShowItunesSearch(true); setSearchQuery(''); }}
                      className="w-full py-3 px-4 rounded-xl border border-dashed border-border text-[13px] text-muted-foreground hover:bg-muted/30 hover:text-foreground hover:border-primary/50 active:scale-[0.99] transition-all text-center"
                    >
                      Can't find it? Request to add a new app to the Shomri App Store →
                    </button>
                  </div>
                </>
              )}

              {/* ── STEP 2: iTunes request flow ── */}
              {showItunesSearch && (
                <>
                  {isItunesSearching && (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                    </div>
                  )}
                  {!isItunesSearching && searchQuery.trim().length < 2 && (
                    <p className="text-center text-[13px] text-muted-foreground py-8">
                      Type at least 2 characters to search
                    </p>
                  )}
                  {!isItunesSearching && searchQuery.trim().length >= 2 && itunesResults.length === 0 && (
                    <p className="text-center text-[13px] text-muted-foreground py-8">
                      No apps found on the Apple App Store
                    </p>
                  )}
                  {!isItunesSearching && itunesResults.length > 0 && (
                    <div className="divide-y divide-border/50">
                      {itunesResults.map((app) => (
                        <div
                          key={app.trackId}
                          className="w-full px-3 py-3 flex items-center gap-3 text-left"
                        >
                          <img
                            src={app.artworkUrl100 || '/placeholder.svg'}
                            alt={app.trackName}
                            className="w-10 h-10 rounded-xl object-cover flex-shrink-0"
                            onError={(e) => { e.currentTarget.src = '/placeholder.svg'; }}
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-[14px] font-medium text-foreground truncate">{app.trackName}</p>
                            {app.primaryGenreName && (
                              <p className="text-[11px] text-muted-foreground truncate">{app.primaryGenreName}</p>
                            )}
                          </div>
                          <button
                            onClick={() => handleRequestItunesApp(app)}
                            disabled={isRequestingApp}
                            className="flex-shrink-0 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-[12px] font-medium hover:bg-primary/90 active:scale-95 transition-all disabled:opacity-50"
                          >
                            {isRequestingApp ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Request'}
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog for Item Requests */}
      <AlertDialog open={showItemConfirmation} onOpenChange={setShowItemConfirmation}>
        <AlertDialogContent className="max-w-[90vw] sm:max-w-md rounded-2xl bg-background">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {canChangeDirectly ? (
                <>
                  {requestAction?.type === 'category' && requestAction?.action === 'block' && `Block "${requestAction.name}"?`}
                  {requestAction?.type === 'category' && requestAction?.action === 'allow' && `Allow "${requestAction.name}"?`}
                  {requestAction?.action === 'add' && requestAction?.type === 'whitelist' && `Whitelist "${requestAction.name}"?`}
                  {requestAction?.action === 'add' && requestAction?.type === 'blacklist' && `Block "${requestAction.name}"?`}
                  {requestAction?.action === 'remove' && requestAction?.type === 'whitelist' && `Remove "${requestAction.name}" from Whitelist?`}
                  {requestAction?.action === 'remove' && requestAction?.type === 'blacklist' && `Remove "${requestAction.name}" from Blacklist?`}
                </>
              ) : (
                <>
                  {requestAction?.type === 'category' && requestAction?.action === 'block' && `Request to Block "${requestAction.name}"`}
                  {requestAction?.type === 'category' && requestAction?.action === 'allow' && `Request to Allow "${requestAction.name}"`}
                  {requestAction?.action === 'add' && requestAction?.type === 'whitelist' && `Request to Whitelist "${requestAction.name}"`}
                  {requestAction?.action === 'add' && requestAction?.type === 'blacklist' && `Request to Block "${requestAction.name}"`}
                  {requestAction?.action === 'remove' && requestAction?.type === 'whitelist' && `Remove "${requestAction.name}" from Whitelist`}
                  {requestAction?.action === 'remove' && requestAction?.type === 'blacklist' && `Remove "${requestAction.name}" from Blacklist`}
                </>
              )}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[14px]">
              {requestAction?.catalogApp && (
                <div className="flex items-center gap-3 mb-3 p-2 bg-muted/30 rounded-xl">
                  <img 
                    src={requestAction.catalogApp.iconUrl || '/placeholder.svg'} 
                    alt={requestAction.catalogApp.name}
                    className="w-12 h-12 rounded-xl"
                    onError={(e) => { e.currentTarget.src = '/placeholder.svg'; }}
                  />
                  <div>
                    <p className="font-medium text-foreground">{requestAction.catalogApp.name}</p>
                    {requestAction.catalogApp.category && (
                      <p className="text-[12px] text-muted-foreground">{requestAction.catalogApp.category}</p>
                    )}
                  </div>
                </div>
              )}
              {requestAction?.type === 'category' && requestAction?.action === 'block' && `Apps in the "${requestAction.name}" category will no longer be available for download.`}
              {requestAction?.type === 'category' && requestAction?.action === 'allow' && `Apps in the "${requestAction.name}" category will become available for download.`}
              {requestAction?.action === 'add' && requestAction?.type === 'whitelist' && 'This app will always be available regardless of category settings.'}
              {requestAction?.action === 'add' && requestAction?.type === 'blacklist' && 'This app will be blocked regardless of category settings.'}
              {requestAction?.action === 'remove' && requestAction?.type === 'whitelist' && `"${requestAction.name}" will no longer be guaranteed available. Its availability will depend on category settings.`}
              {requestAction?.action === 'remove' && requestAction?.type === 'blacklist' && `"${requestAction.name}" will no longer be blocked. Its availability will depend on category settings.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 sm:gap-0">
            <AlertDialogCancel className="rounded-xl" disabled={isSaving}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => {
                handleConfirmItemRequest();
              }}
              className="rounded-xl bg-primary text-primary-foreground"
              disabled={isSaving}
            >
              {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : canChangeDirectly ? 'Confirm' : 'Submit Request'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AppManagement;